import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

export function Sheet({ open, onClose, title, children }: SheetProps) {
  if (!open) return null;
  // Portalled to document.body rather than rendered in place: `fixed
  // inset-0` only covers the true viewport when nothing between it and
  // <body> establishes its own containing block - but a `backdrop-filter`
  // (e.g. FormSection's `backdrop-blur-xl`, which every caller of this
  // component sits inside) does exactly that, same as `transform`/
  // `filter`/`perspective` would. Without the portal, a Sheet opened from
  // inside one of those gets squashed into that ancestor's small box
  // instead of covering the screen - no backdrop, no visible header, no
  // way to close it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl bg-white dark:bg-[#1c1c1e] max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-black/10 dark:border-white/10 bg-white dark:bg-[#1c1c1e]">
          <span className="font-semibold text-[15px]">{title}</span>
          <button onClick={onClose} className="text-black/40 dark:text-white/40 hover:text-current">
            <X size={20} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}
